import api from 'api';
import { sampleRandomColorFromPalette } from 'constants/colorPalette';
import { errorMessageFactory } from 'factories/api';
import type { TeamWithUsers } from 'factories/teams';
import { teamDraftFactory } from 'factories/teams';
import { mapValues, merge } from 'lodash-es';
import { normalize } from 'normalizr';
import { showToast } from 'redux/modules/toasts.actions';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import { TEAM_SCHEMA_KEY, teamListSchema, teamSchema } from 'schema/teams';
import { TrackingActions } from 'tracking/actions';
import type { Thunk } from 'types/redux';
import type { Team } from 'types/team';
import type { DeepReadonly } from 'types/utility';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { fetchPaginated } from 'utils/api';
import { rewriteEmojiPath } from 'utils/emojis';
import type { State, TeamSchemaPayload, TeamsSchemaPayload } from './teams.types';
import {
  addTeam,
  addTeamLeadToTeamDraft,
  addUserToTeamDraft,
  deleteTeam,
  editTeam,
  fetchedTeam,
  removeTeamLeadFromTeamDraft,
  removeUserFromTeamDraft,
  toggleTeamsModal,
  updateTeamDraft,
  withXhrStatusReducer,
  wrapFetching,
  wrapSaving,
} from './teams.types';

export const initialState: State = {
  draft: null,
  entities: {
    [TEAM_SCHEMA_KEY]: {},
  },
  result: [],
  showTeamsManagementModal: false,
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState<DeepReadonly<State>>(initialState)
  .case(fetchedTeam, (state, payload) => {
    const newState = {
      ...state,
      ...payload,
    };
    const entities = payload?.entities;
    if (entities) {
      const newEntities = {
        team: mapValues(entities.team, team => rewriteEmojiPath(team)),
      };
      newState.entities = merge({}, state.entities, newEntities);
    }

    return newState;
  })
  .case(updateTeamDraft, (state, draft) => {
    if (draft == null) {
      return {
        ...state,
        draft,
      };
    }
    return {
      ...state,
      draft: teamDraftFactory(draft),
    };
  })
  .case(addUserToTeamDraft, (state, user) => {
    if (!state.draft) return state;
    const draftUsers = state.draft.users ?? [];
    let newUsers;
    if (user.isTeamLead && user.hide_hours === true) {
      // if a user is already present in the list as a manager, but not as a team member (not part of the team)
      newUsers = draftUsers.map(u =>
        u.user_id === user.id
          ? {
              ...u,
              hide_hours: false,
            }
          : u
      );
    } else {
      const user_id = user.id;
      newUsers = [...draftUsers, { user_id }];
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        users: newUsers,
      },
    };
  })
  .case(removeUserFromTeamDraft, (state, user) => {
    if (!state.draft) return state;
    const userId = user.id;

    const users = state.draft.users
      .filter(
        // filter out managers who are not team members
        ({ user_id }) =>
          user_id !== userId || (user_id === userId && user.isTeamLead && !user.hide_hours)
      )
      .map(
        // but if a manager was a team member -> remove him/her from the team but keep them as a manager
        u =>
          u.user_id === userId && user.isTeamLead && !user.hide_hours
            ? { ...u, hide_hours: true }
            : u
      );
    return {
      ...state,
      draft: {
        ...state.draft,
        users,
      },
    };
  })
  .case(addTeamLeadToTeamDraft, (state, user) => {
    if (!state.draft) return state;
    const userId = user.id;
    let users = state.draft.users;
    if (users.find(u => u.user_id === userId) != null) {
      users = users.map(u => {
        if (u.user_id === userId) {
          return {
            ...u,
            lead: true,
          };
        }
        return u;
      });
    } else {
      users = [...(state.draft.users ?? []), { user_id: userId, lead: true, hide_hours: true }];
    }
    return {
      ...state,
      draft: {
        ...state.draft,
        users,
      },
    };
  })
  .case(addTeam, (state, payload) => {
    const teamId = payload.result;
    const team = payload.entities[TEAM_SCHEMA_KEY][teamId];

    return {
      ...state,
      result: [...state.result, teamId],
      entities: {
        ...state.entities,
        [TEAM_SCHEMA_KEY]: {
          ...state.entities[TEAM_SCHEMA_KEY],
          [teamId]: team,
        },
      },
    };
  })
  .case(editTeam, (state, payload) => {
    const teamId = payload.result;
    return {
      ...state,
      entities: {
        ...state.entities,
        [TEAM_SCHEMA_KEY]: {
          ...state.entities[TEAM_SCHEMA_KEY],
          [teamId]: {
            ...state.entities[TEAM_SCHEMA_KEY][teamId],
            ...payload.entities[TEAM_SCHEMA_KEY][teamId],
          },
        },
      },
    };
  })
  .case(deleteTeam, (state, deleteId) => {
    // Clone team entities & remove deleted team
    const teamEntities = { ...state.entities[TEAM_SCHEMA_KEY] };
    delete teamEntities[deleteId];
    // Remove team id from lookup array
    const result = state.result.filter(id => id !== deleteId);

    return {
      ...state,
      result,
      entities: {
        ...state.entities,
        [TEAM_SCHEMA_KEY]: teamEntities,
      },
    };
  })
  .case(toggleTeamsModal, (state, showTeamsManagementModal) => {
    return {
      ...state,
      showTeamsManagementModal,
    };
  })
  .case(removeTeamLeadFromTeamDraft, (state, user) => {
    if (!state.draft) return state;
    const userId = user.id;

    const users = state.draft.users
      .filter(u => !(u.user_id === userId && u.lead && u.hide_hours)) // if a manager was not part of the team, remove them completely
      .map(u => {
        // but if a manager was a part of the team, leave them as a team member
        if (u.user_id === userId) {
          return {
            ...u,
            lead: false,
          };
        }
        return u;
      });
    return {
      ...state,
      draft: {
        ...state.draft,
        users,
      },
    };
  });

export default withXhrStatusReducer(reducer);

export function createTeamDraft(teamSeed: Partial<TeamWithUsers> = {}) {
  // Keep track of how many times the user starts creation process:
  TrackingActions.initiateUserAction('New Team');

  // Assign random color
  const color = sampleRandomColorFromPalette();

  const draft = teamDraftFactory({
    color,
    ...teamSeed,
  });

  return updateTeamDraft(draft);
}

export function clearTeamDraft(): Thunk {
  return dispatch => {
    dispatch(updateTeamDraft(null));
  };
}

export function handleUpdateTeamDraft(team: Partial<TeamWithUsers>): Thunk {
  return (dispatch, getState) => {
    const draft = {
      ...getState().teams.draft,
      ...team,
    };

    dispatch(updateTeamDraft(draft));
  };
}

export function deleteTeamId(id: number): Thunk<Promise<void>> {
  return async dispatch => {
    dispatch(deleteTeam(id));
    try {
      await dispatch(wrapSaving(api.deleteTeam(id)));
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}

export function saveTeamDraft(
  draft?: Partial<TeamWithUsers>,
  options?: {
    add_users_to_team_projects: boolean;
    delete_users_from_team_projects: boolean;
  }
): Thunk<Promise<Team>> {
  return async (dispatch, getState) => {
    // Prepare draft
    const completeDraft = teamDraftFactory({
      ...getState().teams.draft,
      ...draft,
    });

    try {
      const data = await dispatch(wrapSaving(api.saveTeam(completeDraft, options)));
      const payload = normalize<Team, TeamSchemaPayload['entities'], TeamSchemaPayload['result']>(
        data,
        teamSchema
      );
      // If draft has id, it means we already have the team in store
      if (completeDraft.id) {
        dispatch(editTeam(payload));
      } else {
        // Fresh team, add to store
        dispatch(addTeam(payload));
      }
      return data;
    } catch (error) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      throw error;
    }
  };
}
export function fetchTeams(): Thunk<Promise<void>> {
  return dispatch => {
    const perPage = 50;
    return dispatch(
      wrapFetching<Array<Team>>(
        fetchPaginated({ perPage }, page => api.getTeams({ page, perPage }))
      )
    )
      .then(data => {
        const payload = normalize<
          Team,
          TeamsSchemaPayload['entities'],
          TeamsSchemaPayload['result']
        >(data, teamListSchema);
        dispatch(fetchedTeam(payload));
      })
      .catch(error => {
        dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
        throw error;
      });
  };
}

export function fetchTeamById(id: number): Thunk<Promise<Team>> {
  return dispatch => {
    return dispatch(wrapFetching(api.getTeamById(id)))
      .then(data => {
        const payload = normalize<Team, TeamSchemaPayload['entities'], TeamSchemaPayload['result']>(
          data,
          teamSchema
        );
        dispatch(editTeam(payload));
        return data;
      })
      .catch(error => {
        dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
        throw error;
      });
  };
}

export function handleToggleTeamsModal(open?: boolean): Thunk {
  return (dispatch, getState) => {
    let payload = open || false;
    if (open == null) {
      payload = !getState().teams.showTeamsManagementModal;
    }
    dispatch(toggleTeamsModal(payload));
  };
}
